// Event Details blocks — landing-side port of the reg-portal handoff contract
// (reg-portal task #31, `src/events/detailsSchema.ts` in its
// task-31 worktree). Pure read/resolve helpers for the operator-authored
// blocks stored at `customization.event.config.details[]`. The editor helpers
// (makeId/makeBlock/changeType/validateDetailsUrl) are portal-only and are
// intentionally not ported — the landing only ever reads.
//
// KEPT IN SYNC BY HAND with the portal contract: cascade rules and row-skip
// rules here are what the parity e2e baselines pin. Change both sides or
// change neither.
import { CANONICAL_LOCALES, hasRichTextContent } from './EventPreview.helpers';

// Defensive read of a stored details value: non-array → [] (the legacy-event
// empty state), non-object rows dropped, object rows passed through untouched.
export function toDetailsArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((row) => typeof row === 'object' && row !== null && !Array.isArray(row));
}

// Safe dotted-path read of `customization?.event?.config?.details`.
export function getDetails(customization) {
  return toDetailsArray(customization?.event?.config?.details);
}

// Cascade rule of the handoff contract, applied to label / content /
// displayAs: map[locale] → map.en → ''. Empty-string and non-string tiers are
// skipped at every step. Unknown maps → ''.
export function resolveLocaleText(map, locale) {
  const tiers = [map?.[locale], map?.en];
  for (const tier of tiers) {
    if (typeof tier === 'string' && tier.length > 0) {
      return tier;
    }
  }
  return '';
}

// Cascade rule: displayAs[locale] → displayAs.en → url → ''. An unset
// Display As shows the URL itself.
export function resolveDisplayAs(block, locale) {
  const tiers = [block.displayAs?.[locale], block.displayAs?.en, block.url];
  for (const tier of tiers) {
    if (typeof tier === 'string' && tier.length > 0) {
      return tier;
    }
  }
  return '';
}

// Canonical locales whose key on `map` holds a non-empty string.
function localesCarryingText(map, into) {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return;
  for (const locale of CANONICAL_LOCALES) {
    const value = map[locale];
    if (typeof value === 'string' && value.length > 0) {
      into.add(locale);
    }
  }
}

// Locales carrying authored details content — any block's non-empty
// label / content / displayAs value (url excluded: language-neutral). Junk
// input → [].
export function getDetailsLocales(details) {
  if (!Array.isArray(details)) {
    return [];
  }
  const carrying = new Set();
  for (const row of details) {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) continue;
    localesCarryingText(row.label, carrying);
    localesCarryingText(row.content, carrying);
    localesCarryingText(row.displayAs, carrying);
  }
  return CANONICAL_LOCALES.filter((l) => carrying.has(l));
}

// The tab strip's clickable set (portal Phase 2 D14): title-enabled locales ∪
// locales carrying detailDescription content ∪ locales carrying details
// content. EN is pinned (always clickable) and the result is in canonical
// order — deterministic regardless of jsonb key order.
export function getCardLocales(title, detailDescription, details) {
  const set = new Set();
  if (title && typeof title === 'object' && !Array.isArray(title)) {
    for (const locale of CANONICAL_LOCALES) {
      if (Object.prototype.hasOwnProperty.call(title, locale)) {
        set.add(locale);
      }
    }
  }
  // staging-r1 N6: hoist the walk (the portal does the same) so the rows are
  // visited once, not once per canonical locale.
  const detailsLocales = getDetailsLocales(details);
  for (const locale of CANONICAL_LOCALES) {
    if (hasRichTextContent(detailDescription?.[locale])) {
      set.add(locale);
    }
    if (detailsLocales.includes(locale)) {
      set.add(locale);
    }
  }
  set.add('en');
  return CANONICAL_LOCALES.filter((l) => set.has(l));
}

// Resolve the stored details into render-ready rows for `locale`:
//   text / textArea → label + content (resolveLocaleText cascade)
//   link           → label + url + displayAsText (resolveDisplayAs cascade)
// A row whose resolved label AND payload are both empty is dropped — empty
// rows are invisible on the landing. Unknown / missing types render as text.
// Junk input → [].
export function pickDetailsRows(customization, locale) {
  const rows = [];
  getDetails(customization).forEach((block, index) => {
    const type = block.type === 'link' || block.type === 'textArea' ? block.type : 'text';
    const key = typeof block.id === 'string' && block.id.length > 0 ? block.id : `row-${index}`;
    const label = resolveLocaleText(block.label, locale);

    if (type === 'link') {
      const url = typeof block.url === 'string' ? block.url : '';
      const displayAsText = resolveDisplayAs(block, locale);
      if (label === '' && displayAsText === '') {
        return;
      }
      rows.push({ key, type, label, url, displayAsText });
      return;
    }

    const content = resolveLocaleText(block.content, locale);
    if (label === '' && content === '') {
      return;
    }
    rows.push({ key, type, label, content });
  });
  return rows;
}
